import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Cpu,
  Plus,
  Copy,
  RefreshCw,
  CheckCircle2,
  CircleAlert,
  Trash2,
} from 'lucide-react';
import { trpc } from '../lib/trpc.ts';
import { Badge } from '../components/Badge.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { Modal } from '../components/Modal.tsx';
import { useConfirm } from '../components/ConfirmDialog.tsx';
import { useToast } from '../components/Toast.tsx';
import { Reveal } from '../components/motion.tsx';
import { relativeTime } from '../lib/format.ts';
import './Crud.css';

export function Controllers() {
  const sites = trpc.site.list.useQuery();
  const controllers = trpc.controller.list.useQuery();
  const utils = trpc.useUtils();
  const confirm = useConfirm();
  const toast = useToast();
  const createSite = trpc.site.create.useMutation({
    onSuccess: () => utils.site.list.invalidate(),
  });
  const register = trpc.controller.register.useMutation({
    onSuccess: () => utils.controller.list.invalidate(),
  });
  const rotateToken = trpc.controller.rotateToken.useMutation();
  const remove = trpc.controller.remove.useMutation({
    onSuccess: () => utils.controller.list.invalidate(),
  });

  const [showForm, setShowForm] = useState(false);
  const [tokenShown, setTokenShown] = useState<{ controllerId: string; token: string } | null>(
    null,
  );

  const noSites = !sites.data || sites.data.length === 0;

  return (
    <div className="stack">
      <Reveal className="crud-head">
        <span className="section-eyebrow">Hardware · Fleet</span>
        <h1 className="crud-title">
          <span className="accent">Controllers</span>
        </h1>
        <span className="crud-sub">
          ATRIUM door controllers, paired to your relay agent. Register one, then copy its token into
          the relay config.
        </span>
      </Reveal>

      {sites.data?.length === 0 && (
        <Reveal delay={0.05} className="card">
          <div className="stack-tight">
            <strong>Step 1 — create a site</strong>
            <span className="dim">A site is a physical location. Controllers belong to sites.</span>
          </div>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const fd = new FormData(e.currentTarget);
              await createSite.mutateAsync({
                name: String(fd.get('name') ?? 'Main site'),
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              });
              (e.target as HTMLFormElement).reset();
            }}
            className="row"
            style={{ marginTop: 8 }}
          >
            <input name="name" placeholder="Site name (e.g. Headquarters)" required />
            <button type="submit" disabled={createSite.isPending}>
              <Plus size={14} />
              Create site
            </button>
          </form>
        </Reveal>
      )}

      <Reveal delay={0.1} className="card">
        <div className="card-header">
          <Cpu size={14} className="muted" />
          <h2>Controllers</h2>
          <span className="spacer" />
          <button onClick={() => setShowForm(true)} disabled={noSites}>
            <Plus size={14} />
            Register controller
          </button>
        </div>

        {controllers.isLoading ? (
          <div className="stack">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skel" style={{ height: 42 }} />
            ))}
          </div>
        ) : !controllers.data || controllers.data.length === 0 ? (
          <EmptyState
            icon={Cpu}
            title="No controllers registered"
            body="Register an A22K, then copy the token into your relay agent's config."
          />
        ) : (
          <table className="table crud-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Model</th>
                <th>Status</th>
                <th>Health</th>
                <th>Last seen</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {controllers.data.map((c) => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 500 }}>
                    <Link to={`/controllers/${c.id}`}>{c.name}</Link>
                  </td>
                  <td>
                    <Badge>{c.model}</Badge>
                  </td>
                  <td>
                    {c.isOnline ? (
                      <Badge tone="ok">
                        <CheckCircle2 size={11} />
                        online
                      </Badge>
                    ) : (
                      <Badge tone="warn">
                        <CircleAlert size={11} />
                        offline
                      </Badge>
                    )}
                  </td>
                  <td>
                    <div className="row" style={{ gap: 4 }}>
                      <Badge tone={c.acPowerOk ? 'ok' : 'danger'}>AC</Badge>
                      <Badge tone={c.batteryOk ? 'ok' : 'warn'}>BAT</Badge>
                      <Badge tone={c.boxTamperOk && c.wallTamperOk ? 'ok' : 'danger'}>TMP</Badge>
                    </div>
                  </td>
                  <td className="dim mono" style={{ fontSize: 12 }}>
                    {c.lastSeenAt ? relativeTime(c.lastSeenAt) : 'never'}
                  </td>
                  <td>
                    <div className="row" style={{ gap: 4 }}>
                      <button
                        className="ghost"
                        aria-label={`Rotate relay token for ${c.name}`}
                        title="Rotate relay token"
                        onClick={async () => {
                          const ok = await confirm({
                            title: 'Rotate relay token?',
                            body: 'The current token will stop working immediately. The relay agent must be updated with the new token.',
                            confirmLabel: 'Rotate token',
                            danger: true,
                          });
                          if (!ok) return;
                          const res = await rotateToken.mutateAsync({ id: c.id });
                          setTokenShown({ controllerId: c.id, token: res.relayToken });
                        }}
                      >
                        <RefreshCw size={14} />
                      </button>
                      <button
                        className="ghost"
                        aria-label={`Remove ${c.name}`}
                        title="Remove"
                        onClick={async () => {
                          const ok = await confirm({
                            title: 'Remove controller?',
                            body: (
                              <>
                                Remove <strong>{c.name}</strong>? This cannot be undone and its doors
                                will become unmanaged.
                              </>
                            ),
                            confirmLabel: 'Remove',
                            danger: true,
                          });
                          if (!ok) return;
                          await remove.mutateAsync({ id: c.id });
                          toast.success('Controller removed', c.name);
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Reveal>

      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title="Register a controller"
        dismissable={!register.isPending}
        maxWidth={480}
      >
        <p className="dim" style={{ marginTop: 0 }}>
          A relay token will be displayed once. Copy it into <code>relay.toml</code>.
        </p>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            const fd = new FormData(e.currentTarget);
            const res = await register.mutateAsync({
              siteId: String(fd.get('siteId')),
              name: String(fd.get('name') ?? 'A22K'),
              model: (fd.get('model') as 'A22K') ?? 'A22K',
              serialNumber: (fd.get('serial') as string) || undefined,
            });
            setShowForm(false);
            setTokenShown({ controllerId: res.controllerId, token: res.relayToken });
          }}
          className="stack"
        >
          <select name="siteId" required>
            {sites.data?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <input name="name" placeholder="Friendly name (e.g. Front door A22K)" required />
          <select name="model" defaultValue="A22K">
            <option value="A22">A22</option>
            <option value="A22K">A22K</option>
            <option value="A22KEC">A22KEC (elevator)</option>
            <option value="ADH10K">ADH10K</option>
          </select>
          <input name="serial" placeholder="Serial number (optional)" />
          <div className="dialog-actions">
            <button type="button" className="ghost" onClick={() => setShowForm(false)}>
              Cancel
            </button>
            <button type="submit" disabled={register.isPending}>
              {register.isPending ? 'Registering…' : 'Register'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        open={!!tokenShown}
        onClose={() => setTokenShown(null)}
        title="Relay token"
        maxWidth={480}
        footer={
          <>
            <button
              className="secondary"
              onClick={() => {
                if (!tokenShown) return;
                void navigator.clipboard.writeText(tokenShown.token);
                toast.success('Copied', 'Relay token copied to clipboard');
              }}
            >
              <Copy size={14} />
              Copy
            </button>
            <button onClick={() => setTokenShown(null)}>Done</button>
          </>
        }
      >
        <p className="dim" style={{ marginTop: 0 }}>
          Copy this into <code>relay.toml</code> (or the simulator's <code>RELAY_TOKEN</code> env
          var). It is shown only once — rotate later if lost.
        </p>
        <div className="crud-secret">{tokenShown?.token}</div>
      </Modal>
    </div>
  );
}
